import React from 'react';
import { SEOLayout } from './SEOLayout';
import { Link } from 'react-router-dom';

export default function HowWeReviewTools() {
  return (
    <SEOLayout
      title="How We Review AI Tools — Vergo's Editorial Methodology"
      description="A transparent walk-through of how Vergo tests, scores, and updates every AI tool in our directory. No paid placements, no sponsored rankings."
      keywords="AI tool reviews, how we review AI, AI tool testing methodology, unbiased AI reviews, AI tool evaluation, editorial standards"
    >
      <article className="prose prose-neutral dark:prose-invert max-w-none">
        <h1>How We Review AI Tools</h1>
        <p className="lead">
          Most "best AI tools" lists on the internet are paid placements with a thin layer of editorial varnish. Vergo isn't.
          This page explains exactly how we test, score, and update every tool in our directory — and where our money comes
          from. If you spot a way we could do this better, please tell us at{' '}
          <a href="mailto:editorial@vergoai.app">editorial@vergoai.app</a>.
        </p>

        <h2>The four-stage review process</h2>
        <p>
          Every tool that enters the Vergo directory passes through the same four stages. The same writer doesn't do all four —
          we deliberately split research, hands-on testing, scoring, and final editing across different people so that no
          single opinion can dominate.
        </p>

        <h3>Stage 1 — Intake and triage</h3>
        <p>
          Tools enter our pipeline three ways: editor discovery (we follow launch trackers, GitHub, and a curated set of
          newsletters), reader submissions via the submit form, and automated discovery for tools that show up across
          multiple trusted sources. Around 60% of submissions are rejected at this stage — usually because the tool is a
          rebranded wrapper around an existing model, hasn't shipped a working product, or is clearly an affiliate funnel.
        </p>

        <h3>Stage 2 — Hands-on testing</h3>
        <p>
          A reviewer creates a real account (paid where the free tier doesn't represent the product) and uses the tool for a
          minimum of <strong>five real tasks</strong> over at least <strong>three days</strong>. The tasks are role-appropriate:
          a coding tool gets coding tasks, a writing tool gets writing tasks, a meeting tool gets actual meetings. We don't
          run synthetic benchmarks for the published rating — those numbers don't match real use.
        </p>
        <p>
          Reviewers record raw notes, screenshots, and outputs as they go. These become the substrate for the published review.
        </p>

        <h3>Stage 3 — Scoring</h3>
        <p>
          We score each tool on four dimensions, weighted as follows:
        </p>
        <ul>
          <li><strong>Capability (40%)</strong> — does it do what it claims, well, on real tasks?</li>
          <li><strong>Value (25%)</strong> — is the price proportional to what you get? Is the free tier honest?</li>
          <li><strong>User experience (20%)</strong> — speed, reliability, interface clarity, documentation, error handling.</li>
          <li><strong>Privacy & trust (15%)</strong> — data handling, training-on-user-data defaults, security posture, transparency about limitations.</li>
        </ul>
        <p>
          The weighted score becomes the 1–5 star rating. We deliberately avoid 0.1-resolution scores like "4.27 stars" —
          ratings round to the nearest half-star, because the precision implied by decimals is fake.
        </p>

        <h3>Stage 4 — Editing and publication</h3>
        <p>
          A second editor reviews the draft for fairness, accuracy, and tone. Vendors are not shown reviews before publication
          and do not get edits or approvals. If a vendor disputes something post-publication, we evaluate the dispute on its
          merits and either correct the review or explain publicly why we didn't.
        </p>

        <h2>How often we update</h2>
        <ul>
          <li><strong>Major model upgrades</strong> (GPT-5, Claude 4, etc.) — re-review within 14 days.</li>
          <li><strong>Pricing changes</strong> — updated within 7 days of detection.</li>
          <li><strong>Routine refresh</strong> — every tool re-evaluated at least once per year.</li>
          <li><strong>Reader-reported issues</strong> — investigated within 48 hours.</li>
        </ul>
        <p>
          Each tool page shows the date of its last review. If you see a date that's gone stale, email us — staleness is
          almost always our fault, not yours.
        </p>

        <h2>Conflicts of interest</h2>
        <p>
          Vergo earns money in three ways: a small optional Pro subscription, affiliate commissions on a subset of tools, and
          display advertising. Here's the firewall:
        </p>
        <ul>
          <li>The list of affiliate partners is decided by our business operations team, never by reviewers.</li>
          <li>Reviewers don't know which tools have affiliate links when they're scoring.</li>
          <li>Affiliate status appears in the tool page footer as a disclosure, but plays no role in placement or ranking.</li>
          <li>We do not offer, accept, or negotiate "sponsored placement," "featured slots," or paid review.</li>
          <li>Ads are served by independent networks and are clearly labelled. Vendors cannot buy editorial coverage by buying ads.</li>
        </ul>

        <h2>Where reader reviews come in</h2>
        <p>
          The star rating you see is editorial. Separately, every tool page displays unverified user reviews and an aggregate
          user score, displayed alongside (not blended with) the editorial rating. We do this so you can spot when the
          professional take and the practitioner take diverge — that's often the most useful signal.
        </p>
        <p>
          We moderate user reviews lightly. We remove obvious spam, vendor astroturfing, and anything defamatory; we leave
          critical reviews alone.
        </p>

        <h2>What we won't review</h2>
        <ul>
          <li>Tools that don't have a working product (waitlist-only launches, vapor demos).</li>
          <li>Tools that are obviously a thin wrapper around an existing model with no real added value.</li>
          <li>Tools whose terms of service make hands-on testing impossible.</li>
          <li>Tools we cannot test without buying — we'll add a "not yet reviewed" status rather than fake a rating.</li>
        </ul>

        <h2>When we get it wrong</h2>
        <p>
          We get things wrong. Pricing pages change quietly, models get downgraded between our test and your visit, and
          sometimes we just miss a feature. Our correction policy:
        </p>
        <ul>
          <li>Factual corrections are made within 48 hours of verification.</li>
          <li>Material corrections (something that would change the rating) include a "Corrected" note dated at the top of the review.</li>
          <li>Major errors get a separate post explaining what we missed and why.</li>
        </ul>

        <h2>How to push back on a review</h2>
        <p>
          If you're a vendor and we got something wrong, email <a href="mailto:editorial@vergoai.app">editorial@vergoai.app</a> with
          specifics. We don't engage with vague "this rating is unfair" notes. We do engage with "your screenshot is from our
          old pricing page" and similar.
        </p>
        <p>
          If you're a reader who disagrees with a review, the user review section on each tool page is the right place — and
          the most useful signal to other readers. We read it.
        </p>

        <div className="not-prose mt-8 flex flex-wrap gap-4">
          <Link to="/" className="text-sm underline hover:text-foreground transition-colors">Browse all AI tools →</Link>
          <Link to="/about" className="text-sm underline hover:text-foreground transition-colors">About Vergo →</Link>
          <Link to="/contact" className="text-sm underline hover:text-foreground transition-colors">Contact the team →</Link>
        </div>
      </article>
    </SEOLayout>
  );
}
